// Helpers for consuming the streaming SSE responses from /api/ai/draft-kb
// and /api/ai/draft-ticket-reply.
//
// The backend writes lines like:
//   data: {"type":"content","delta":"..."}\n\n
//   data: {"type":"done","usage":{...}}\n\n
//   data: {"type":"error","message":"..."}\n\n
//
// This helper streams the response, parses each frame, and invokes callbacks.

export const streamAiChat = async (path, body, { onContent, onDone, onError, onFrame, onAbort, signal } = {}) => {
  const token = localStorage.getItem('token');
  let response;
  try {
    response = await fetch(`/api${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Zone-Client': 'web',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(body || {}),
      signal
    });
  } catch (err) {
    // The fetch sat OUTSIDE the try block below, which only ever guarded the
    // read loop. An abort landing before the response headers arrive rejects
    // here, so the promise this function returns rejected too - and no caller
    // awaits it. The result was an unhandled rejection and a loading flag that
    // was never cleared, which is what left Ask Zone dead until a page reload.
    if (err.name === 'AbortError') {
      onAbort?.();
      return { ok: false, aborted: true };
    }
    onError?.(err);
    return { ok: false };
  }

  if (!response.ok) {
    // Mirror the axios 401 interceptor: an expired/invalid token would
    // otherwise just surface as a small red "Request failed: 401" line and
    // the user wouldn't know why Ask Zone went quiet. Clear the token and
    // let the auth state redirect to login.
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      onError?.(new Error('Your session expired — please sign in again.'));
      if (typeof window !== 'undefined') {
        setTimeout(() => window.location.reload(), 800);
      }
      return { ok: false };
    }
    let message = `Request failed: ${response.status}`;
    try {
      const data = await response.json();
      if (data?.message) message = data.message;
    } catch {
      // ignore
    }
    onError?.(new Error(message));
    return { ok: false };
  }

  if (!response.body || !response.body.getReader) {
    onError?.(new Error('Streaming is not supported in this browser'));
    return { ok: false };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let usage = null;

  const flushFrames = () => {
    let idx;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const line = frame.split('\n').find(l => l.startsWith('data:'));
      if (!line) continue;
      const payload = line.slice(5).trim();
      if (!payload) continue;
      try {
        const obj = JSON.parse(payload);
        onFrame?.(obj);
        if (obj.type === 'content') {
          onContent?.(obj.delta || '');
        } else if (obj.type === 'done') {
          usage = obj.usage || null;
        } else if (obj.type === 'error') {
          onError?.(new Error(obj.message || 'AI error'));
        }
      } catch {
        // ignore
      }
    }
  };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      flushFrames();
    }
    buffer += decoder.decode();
    flushFrames();
    onDone?.({ usage });
    return { ok: true, usage };
  } catch (err) {
    if (err.name === 'AbortError') {
      onAbort?.();
      return { ok: false, aborted: true };
    }
    onError?.(err);
    return { ok: false };
  }
};
